'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import styles from './searchCategories.module.scss';

import { AggregationValue, AggregationGroup } from '../searchFilters';

interface SearchCategoriesProps {
  loading: boolean;
  aggs: AggregationGroup[];
}

import { CATEGORY_IMAGE_MAP, CATEGORY_URL_MAP, LABEL_NORMALIZATION_MAP, } from '@/lib/constants/categoryMaps';

const SearchCategories: React.FC<SearchCategoriesProps> = ({ loading, aggs }) => {
  const searchParams = useSearchParams();
  const query = searchParams.get('q') ?? '';

  if (loading) return null;

  const categoryGroup = aggs.find(group => group.keyword === 'category.keyword');
  if (!categoryGroup || !Array.isArray(categoryGroup.values)) return null;

  const categories = categoryGroup.values
    .filter((item): item is AggregationValue => !!item.count && typeof item.label === 'string')
    .map(item => {
      const normalizedName = LABEL_NORMALIZATION_MAP[item.label] ?? item.label;
      const imageMeta = CATEGORY_IMAGE_MAP[normalizedName];

      return {
        name: normalizedName,
        count: item.count,
        href: `${CATEGORY_URL_MAP[normalizedName]}${query ? `?q=${encodeURIComponent(query)}` : ''}`,
        image: imageMeta?.src,
        width: imageMeta?.width,
        height: imageMeta?.height,
      };
    })
    .filter(item => item.image && item.count > 0);

  if (categories.length === 0) return null;

  return (
    <div className={styles.categories}>
      {categories.map((category, index) => (
        <div className={styles.category} key={index}>
          <Link href={category.href}>
            <div className={styles.imageWrapper}>
              <Image
                src={category.image}
                alt=''
                width={category.width}
                height={category.height}
              />
            </div>
            <div className={styles.categoryName}>{category.name}</div>
          </Link>
          <div className={styles.categoryCount}>({category.count} Products)</div>
        </div>
      ))}
    </div>
  );
};

export default SearchCategories;
